import { z } from 'zod';
import { uuid, isoDateTime } from './common.js';
export const printerKind = z.enum(['pdf', 'raw']);
/**
 * Auto-print rules: when `trigger` happens in `warehouseId` (or any warehouse when null), print
 * `document` on `printerId`. Triggers available now: an order released to picking (pick note), a
 * fulfilment shipped (delivery note), a batch created (one or all four batch documents).
 */
export const autoPrintTrigger = z.enum(['fulfilment_released', 'fulfilment_shipped', 'batch_created', 'shipment_booked']);
export const autoPrintDocument = z.enum(['pick_note', 'delivery_note', 'batch_all', 'batch_overview', 'batch_pick_list', 'batch_totals', 'batch_gift_list', 'shipping_label', 'customs_invoice']);
export const autoPrintRuleDto = z.object({
    id: z.string().uuid(),
    trigger: autoPrintTrigger,
    document: autoPrintDocument,
    warehouseId: z.string().uuid().nullable(),
    warehouseCode: z.string().nullable(),
    printerId: z.string().uuid(),
    printerName: z.string(),
    copies: z.number().int(),
    enabled: z.boolean(),
});
export const createAutoPrintRuleRequest = z.object({
    trigger: autoPrintTrigger,
    document: autoPrintDocument,
    warehouseId: z.string().uuid().nullable().default(null),
    printerId: z.string().uuid(),
    copies: z.number().int().min(1).max(10).default(1),
    enabled: z.boolean().default(true),
});
export const updateAutoPrintRuleRequest = createAutoPrintRuleRequest.partial();
export const printerRole = z.enum(['delivery_note', 'shipping_label', 'mini_label']);
export const documentFormat = z.enum(['pdf', 'zpl']);
export const printJobStatus = z.enum(['queued', 'leased', 'printed', 'failed', 'cancelled']);
/** What the agent reports about one printer it can see. */
export const agentPrinterReport = z.object({
    osName: z.string().min(1).max(200),
    driver: z.string().max(200).optional(),
    kindGuess: printerKind.optional(),
    online: z.boolean().optional(),
});
/** Download of the self-installing agent: the name it gets in the admin, optionally its warehouse. */
export const agentInstallerQuery = z.object({ name: z.string().trim().min(1).max(80), warehouseId: z.string().uuid().optional() });
export const agentEnrolRequest = z.object({
    enrolmentCode: z.string().trim().min(6).max(64),
    name: z.string().trim().min(1).max(100),
    os: z.string().max(100),
    version: z.string().max(40),
});
export const agentEnrolResponse = z.object({
    agentId: uuid,
    token: z.string(),
});
export const agentPrintersRequest = z.object({
    printers: z.array(agentPrinterReport),
});
export const agentHeartbeatRequest = z.object({
    version: z.string().max(40),
    printers: z.array(z.object({ osName: z.string(), online: z.boolean() })).optional(),
});
/** A job as handed to the agent. */
export const agentJob = z.object({
    id: uuid,
    printerOsName: z.string(),
    printerAddress: z.string().nullable(),
    format: documentFormat,
    copies: z.number().int().min(1).max(50),
    options: z
        .object({
        duplex: z.boolean().optional(),
        fitToPage: z.boolean().optional(),
        media: z.string().optional(),
    })
        .default({}),
    documentUrl: z.string().url(),
    leaseExpiresAt: isoDateTime,
});
export const agentAckRequest = z.object({
    status: z.enum(['printed', 'failed']),
    error: z.string().max(2000).optional(),
    printedAt: isoDateTime.optional(),
});
export const printerDto = z.object({
    id: uuid,
    agentId: uuid,
    agentName: z.string(),
    warehouseId: uuid.nullable(),
    osName: z.string(),
    displayName: z.string(),
    kind: printerKind,
    address: z.string().nullable(),
    media: z.string().nullable(),
    enabled: z.boolean(),
    online: z.boolean(),
});
export const updatePrinterRequest = z.object({
    displayName: z.string().trim().min(1).max(100).optional(),
    kind: printerKind.optional(),
    address: z.string().trim().max(200).nullable().optional(),
    media: z.string().trim().max(40).nullable().optional(),
    enabled: z.boolean().optional(),
});
export const printJobDto = z.object({
    id: uuid,
    printerId: uuid,
    printerName: z.string(),
    documentId: uuid.nullable(),
    documentType: z.string(),
    format: documentFormat,
    copies: z.number().int(),
    status: printJobStatus,
    attempts: z.number().int(),
    lastError: z.string().nullable(),
    requestedBy: z.string().nullable(),
    createdAt: isoDateTime,
    printedAt: isoDateTime.nullable(),
});
export const createTestPrintRequest = z.object({
    printerId: uuid,
    format: documentFormat,
});
export const agentDto = z.object({
    id: uuid,
    name: z.string(),
    warehouseId: uuid.nullable(),
    os: z.string(),
    version: z.string(),
    status: z.enum(['online', 'offline']),
    lastSeenAt: isoDateTime.nullable(),
    enrolledAt: isoDateTime,
    printerCount: z.number().int(),
});
//# sourceMappingURL=printing.js.map